import type { components, paths } from '@social-studio/operator-client';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { api, call } from '../../lib/api';
import { saveFile } from '../../lib/download';

export type TenantSummary = components['schemas']['TenantSummaryDto'];
export type BillingState = TenantSummary['billing_state'];
export type IndustryProfile = TenantSummary['industry_profile'];
type ListQuery = NonNullable<paths['/v1/operator/tenants']['get']['parameters']['query']>;
export type TenantSort = NonNullable<ListQuery['sort']>;

export const BILLING_STATES: readonly BillingState[] = ['metered', 'exempt', 'none', 'unlinked', 'unknown'];
export const INDUSTRY_PROFILES: readonly IndustryProfile[] = ['tourism', 'general'];
export const TENANT_SORTS: readonly TenantSort[] = ['name', 'created_at', 'credits_month', 'last_activity'];

export interface TenantFilter {
  readonly q: string;
  readonly industryProfile: IndustryProfile | '';
  readonly billingState: BillingState | '';
  readonly sort: TenantSort;
}

const PAGE_SIZE = 50;

function toQuery(filter: TenantFilter) {
  return {
    sort: filter.sort,
    ...(filter.q.trim() ? { q: filter.q.trim() } : {}),
    ...(filter.industryProfile ? { industry_profile: filter.industryProfile } : {}),
    ...(filter.billingState ? { billing_state: filter.billingState } : {}),
  };
}

/** The tenant list page by page (REQ-140-011); the server filters and sorts. */
export function useTenants(filter: TenantFilter) {
  return useInfiniteQuery({
    queryKey: ['operator-tenants', toQuery(filter)],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      call(() => api.GET('/v1/operator/tenants', { params: { query: { ...toQuery(filter), limit: PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) } } })),
    getNextPageParam: (page) => page.next_cursor ?? undefined,
  });
}

/** Downloads the filtered list as CSV (REQ-OPS-011). */
export function useTenantExport() {
  return useMutation({
    mutationFn: async ({ filter, fileName }: { filter: TenantFilter; fileName: string }) => {
      const text = await call(() => api.GET('/v1/operator/tenants/export', { params: { query: toQuery(filter) }, parseAs: 'text' }));
      saveFile(new Blob([text as string], { type: 'text/csv;charset=utf-8' }), fileName);
    },
  });
}
